import i18n from '@/i18n';
import { VisualTheme } from '@/lib/enum/Colour';
import {
  isMissing,
  withinLateGracePeriod,
} from '@/lib/schedule-v2/shiftV2Status';
import { isFuture, isPast, minutesBetweenDates } from '@/util/dateArithmetic';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import {
  LeavePolicy,
  LeaveType,
  Shift,
  TimesheetEntry,
  TimesheetEntryStatusEnum,
} from '../../../api/v1';

export const timeClockStatus = (
  timezone,
  shift: Shift | null,
  timesheet: TimesheetEntry | null,
  gracePeriod: number,
): {
  label: string;
  colour: string;
  labelColour: string;
  borderColour: string;
  dotColour: VisualTheme;
} => {
  // NOTE: The order of the below status properties matters, top of the list is highest priority

  // Due in - no timesheet, before shift start time
  const dueIn = !timesheet && shift && isFuture(shift.startsAt);
  // Late - no timesheet after shift start time before clock in grace period
  const late =
    !timesheet &&
    shift &&
    isMissing(shift, timesheet) &&
    withinLateGracePeriod(shift, gracePeriod, timezone);
  const absent = !!(!timesheet && shift && shift.absenceId);
  // Not clocked in - no timesheet after the clock in grace period
  const notClockedIn =
    shift &&
    isMissing(shift, timesheet) &&
    !withinLateGracePeriod(shift, gracePeriod, timezone) &&
    isFuture(shift.endsAt);
  // Did not clock in - after shift end time and is missing
  const didNotClockIn =
    shift && isMissing(shift, timesheet) && isPast(shift.endsAt);
  // Clocked-in - timesheet in progress
  const clockedIn =
    timesheet && timesheet.status === TimesheetEntryStatusEnum.Started;
  // Break - timesheet on break
  const onBreak =
    timesheet && timesheet.status === TimesheetEntryStatusEnum.Break;
  // Clocked-out - timesheet completed
  const clockedOut =
    timesheet && timesheet.status === TimesheetEntryStatusEnum.Completed;

  const minutesUntilShiftStarts = shift
    ? minutesBetweenDates(shift.startsAt, new Date(), timezone)
    : 0;

  const label = {
    [i18n.tc('clockIn.status.absent')]: absent,
    [i18n.tc('clockIn.status.startsIn', 1, {
      minutes: minutesUntilShiftStarts,
    })]: minutesUntilShiftStarts < 15 && dueIn,
    [i18n.tc('clockIn.status.clockedOut')]: clockedOut,
    [i18n.tc('clockIn.status.clockedIn')]: clockedIn,
    [i18n.tc('clockIn.status.break')]: onBreak,
    [i18n.tc('clockIn.status.late')]: late,
    [i18n.tc('clockIn.status.notClockedIn')]: notClockedIn,
    [i18n.tc('clockIn.status.didNotClockIn')]: didNotClockIn,
  };

  const labelColour = {
    'gray-700': dueIn || clockedOut,
    green: onBreak,
    white: true,
  };

  const borderColour = {
    green: onBreak,
    none: true,
  };

  const colour = {
    gray: dueIn || clockedOut,
    green: clockedIn || onBreak,
    yellow: late,
    red: didNotClockIn || notClockedIn,
  };

  const dotColour = {
    [VisualTheme.Disabled]: clockedOut,
    [VisualTheme.Success]: clockedIn || onBreak,
    [VisualTheme.Warning]: late,
    [VisualTheme.Danger]: notClockedIn || didNotClockIn,
  };

  return {
    // From the object keys cycle through the object until hit a true condition unless null
    label: keyOfFirstTruthyValue(label) as string,
    colour: keyOfFirstTruthyValue(colour) as string,
    labelColour: keyOfFirstTruthyValue(labelColour) as string,
    borderColour: keyOfFirstTruthyValue(borderColour) as string,
    dotColour: keyOfFirstTruthyValue(dotColour),
  };
};

export const isScheduledBreakClocked = (
  timesheet: TimesheetEntry,
  scheduledBreakId: number,
): boolean => {
  if (!timesheet) {
    throw new Error('Timesheet Entry should be present');
  }

  return !!timesheet.timesheetBreaks.find(
    (b) => b.scheduledBreakId === scheduledBreakId,
  );
};

export const isScheduledBreakInProgress = (
  timesheet: TimesheetEntry,
  scheduledBreakId: number,
): boolean => {
  const timesheetBreak = timesheet.timesheetBreaks.find(
    (b) => b.scheduledBreakId === scheduledBreakId,
  );
  return timesheetBreak ? !timesheetBreak.endedAt : false;
};

export const approveAllButtonText = (
  bulkEntryIdsToApprove: number[],
  unapprovedEntries: TimesheetEntry[],
  timesheetEntries: TimesheetEntry[],
  entriesWithConflicts: TimesheetEntry[],
): string => {
  const approvableTotals = `${bulkEntryIdsToApprove.length}/${unapprovedEntries.length}`;
  const approvedTotalsExcludingConflicts = `${
    timesheetEntries.length - unapprovedEntries.length
  }/${timesheetEntries.length}`;

  return (
    (keyOfFirstTruthyValue({
      // no conflicts
      [i18n.tc('button.approveEntries', bulkEntryIdsToApprove.length)]:
        unapprovedEntries.length && !entriesWithConflicts.length,
      // conflicts & none approved / conflicts & some approved
      [i18n.tc('button.approveEntries', 2, {
        n: approvableTotals,
      })]:
        !!unapprovedEntries.length &&
        !!entriesWithConflicts.length &&
        !!bulkEntryIdsToApprove.length,
      // conflicts & all approvable entries already approved
      [i18n.tc('button.approvedEntries', 1, {
        n: approvedTotalsExcludingConflicts,
      })]: unapprovedEntries.length && !bulkEntryIdsToApprove.length,
      // all approved
      [i18n.tc('button.allEntriesApproved')]: !unapprovedEntries.length,
    }) as string) ?? ''
  );
};

export const isEntitledToPublicHolidays = (
  employeeLeavePolicy: LeavePolicy | null,
  publicHolidayLeaveType: LeaveType | undefined,
): boolean => {
  if (!employeeLeavePolicy || !publicHolidayLeaveType) {
    return false;
  }

  return employeeLeavePolicy.leaveTypeIds.includes(publicHolidayLeaveType.id);
};
